// SkillsPanel — inventory of available skills (built-in, user, global).
// Read-only for v1: server has skills.save but exposing an editor is a
// separate UX surface; this panel lets the user see what's there.

import { useEffect, useState } from "preact/hooks";
import { api, type Skill } from "../../lib/rpc";
import { LibraryPanel, LibraryRow, LibraryEmpty } from "./library-panel";

export function SkillsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.skillsList()
      .then((list) => { if (!cancelled) setSkills(list); })
      .catch(() => { if (!cancelled) setSkills([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const current = skills.find((s) => s.name === active) || null;

  return (
    <LibraryPanel
      open={open}
      onClose={onClose}
      title="Skills"
      icon="sparkle"
      sub={`${skills.length} available · always on`}
      list={
        loading
          ? <p className="lib-loading">loading…</p>
          : skills.length === 0
            ? <p className="lib-loading">No skills yet.</p>
            : skills.map((s) => (
                <LibraryRow
                  key={s.name}
                  active={active === s.name}
                  name={"/" + s.name}
                  description={s.description}
                  badge={s.scope === "global" ? "global" : s.scope === "builtin" ? "builtin" : undefined}
                  onClick={() => setActive(s.name)}
                />
              ))
      }
      detail={
        current
          ? <SkillDetail s={current} />
          : <LibraryEmpty icon="sparkle" headline="Pick a skill" sub="A read-only view of the body the agent uses." />
      }
    />
  );
}

function SkillDetail({ s }: { s: Skill }) {
  return (
    <div className="lib-detail-pane">
      <div className="lib-detail-head">
        <code className="lib-detail-title">/{s.name}</code>
        {s.scope && <span className="lib-detail-scope">{s.scope}</span>}
      </div>
      {s.description && <p className="lib-detail-desc">{s.description}</p>}
      {s.argumentHint && (
        <div className="lib-detail-row">
          <span className="lib-detail-label">usage</span>
          <code>/{s.name} {s.argumentHint}</code>
        </div>
      )}
      <div className="lib-detail-label">body</div>
      <pre className="lib-detail-body">{s.body || "(empty)"}</pre>
    </div>
  );
}
